import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ROUTE_TYPE, PACKAGE_SIZE, PACKAGE_TYPE, BRAND_FAVICON } from 'src/app/constants';
import * as moment from 'moment';
import { setFormTouched } from 'src/app/utils/form';
import { AlertService } from 'src/app/services/alert/alert.service';
import { NavigationEnd, Router } from '@angular/router';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service'
import { FirebaseService } from 'src/app/services/firebase-analytics/firebase.service';
import { ChatAIService } from 'src/app/services/chat-ai/chat-ai.service';
import { from, Subscription } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { SendObjectsPage, IRouteObjectDTO } from './send-objects/send-objects.page';
import { AlertController, ModalController } from '@ionic/angular';
import { Storage } from '@ionic/storage';

const INPUT_FORMAT = 'DD/MM/YYYY';
const MAX_PACKAGE_WEIGHT = 75 * 10 * 10 * 10;
const MAX_PACKAGE_NUMBERS = 50 * 10 * 10 * 10;

@Component({
  selector: 'app-send',
  templateUrl: './send.page.html',
  styleUrls: ['./send.page.scss'],
})
export class SendPage implements OnInit, OnDestroy {
  form: FormGroup;
  
  packageTypes = Object.keys(PACKAGE_TYPE);
  packageSizes = Object.keys(PACKAGE_SIZE);

  minDate = new Date();
  date = moment();

  verboseFromProvince: string;
  verboseFromRegion: string;
  verboseToProvince: string;
  verboseToRegion: string;

  programmaticRouteSub: Subscription;
  lastPageSub: Subscription;
  routerSub: Subscription;
  showAI: boolean;
  
  mascotteAvatar: string;
  isLargerScreen: boolean = false;

  fromCountryCode: string;
  toCountryCode: string;
  
  constructor(
    private alert: AlertService,
    private fb: FormBuilder,
    private router: Router,
    private googleAnalyticsService: GoogleAnalyticsService,
    private firebaseService: FirebaseService,
    private chatAIService: ChatAIService,
    private alertController: AlertController,
    private storage: Storage,
    private modalController: ModalController, // Aggiungi questo
    ) {
    this.mascotteAvatar = BRAND_FAVICON;
    this.form = this.fb.group({
      type: [ROUTE_TYPE.carry, Validators.required],
      from: ['', [Validators.required]],
      to: ['', Validators.required],
      date: ['', Validators.required],
      arrivalDate: ['', Validators.required],
      packageType: [],
      packagesNumber: [, [Validators.min(0), Validators.max(MAX_PACKAGE_NUMBERS)]],
      packageSize: [''],
      packageWeight: [, [Validators.required, Validators.min(1), Validators.max(MAX_PACKAGE_WEIGHT)]],
      packageFragile: [false],
      packageFlammable: [false],
      gpsEnabled: [false],
      professionalCarrier: [false],
      shippingTermsAndConditions: [false, Validators.requiredTrue],
      fromCountryCode: [''],
      toCountryCode: [''],
      sendObjects: [[], Validators.required]
    });
    this.selectedDate(moment());
    this.showAI = true;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.screenSizeIsLargerThen();
  }
  
  ngOnInit() {
    this.screenSizeIsLargerThen();

    // Carica i sendObjects salvati in precedenza
    // this.loadSendObjectsFromStorage();

    this.routerSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd && event.url.includes('/tabs/send')) {
        // this.handleNavigationState();
      }
    });

    this.lastPageSub = this.chatAIService.lastPage$.pipe(
      filter((url: string) => url === '/chat-ai'),
      switchMap(() => this.chatAIService.programmaticRoute$)
    ).subscribe({
        next:(route: any) => {
        if (route) {
          const { stepsFrom = {}, stepsTo = {} } = route;

          this.form.patchValue(route);
          
          this.selectedArrivalDate(moment(route.arrivalDate));
          this.selectedDate(moment(route.date));
          this.initRouteSteps(stepsFrom, stepsTo);
          console.log('Route content', route);
          this.fromCountryCode = route.fromCountryCode;
          this.toCountryCode = route.toCountryCode;
        }
      },
      error: async (error) => { console.log('error', error); },
      complete: () => {},
    });

    this.loadSendObjectsFromStorage();

    // this.openSendObjects();
  }

  ngOnDestroy() {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }

    if (this.lastPageSub) {
      this.lastPageSub.unsubscribe();
    }

    if (this.programmaticRouteSub) {
      this.programmaticRouteSub.unsubscribe();
    }
  }

  screenSizeIsLargerThen() {
    this.isLargerScreen = window.innerWidth > 550;
  }

  async isReleased() {
    try {
      const innerValue = await this.chatAIService.isReleased();
      this.showAI = innerValue === 'show';
    } catch(error) {
      this.showAI = false;
    }
  }

  selectedFrom(place) {
    const { description = '', province = '', region = '', countryShortName = '' } = place || {};
    this.form.get('from').setValue(description);

    this.verboseFromProvince = province;
    this.verboseFromRegion = region;
    this.form.get('fromCountryCode').setValue(countryShortName);
  }

  selectedTo(place) {
    const { description = '', province = '', region = '', countryShortName = '' } = place || {};
    this.form.get('to').setValue(description);
    this.verboseToProvince = province;
    this.verboseToRegion = region;
    this.form.get('toCountryCode').setValue(countryShortName);
  }

  hasSelectedFrom() {
    return !!this.form.get('from').value;
  }

  hasSelectedTo() {
    return !!this.form.get('to').value;
  }

  selectedDate(date: moment.Moment) {
    this.date = date;
    this.form.get(`date`).setValue(date.format(INPUT_FORMAT));

    const arrivalDateValue = this.form.get(`arrivalDate`).value;

    // TIP If arrival is before trip date, set it null
    if (arrivalDateValue) {
      if (moment(arrivalDateValue, INPUT_FORMAT).isBefore(date, 'day')) {
        this.form.get(`arrivalDate`).setValue(null);
       }
    }
  }

  selectedArrivalDate(date: moment.Moment) {
    this.form.get(`arrivalDate`).setValue(date.format(INPUT_FORMAT));
  }

  resetForm() {
    this.form.patchValue({
      from: '',
      to: '',
      date: '',
      arrivalDate: '',
      packageType: null,
      packagesNumber: null,
      packageSize: null,
      packageWeight: null,
      packageFragile: false,
      packageFlammable: false,
      gpsEnabled: false,
      professionalCarrier: false,
      shippingTermsAndConditions: false,
      sendObjects: [],
      fromCountryCode: '',
      toCountryCode: ''
    });
    
    // Cancella anche i dati dallo storage
    this.clearSendObjectsFromStorage();
  }

  async onSubmit() {
    // Controllo specifico per sendObjects vuoto
    const sendObjects = this.form.get('sendObjects').value;
    if (!sendObjects || sendObjects.length === 0) {
      this.form.get('sendObjects').setErrors({ required: true });
      this.form.get('sendObjects').markAsTouched();
    }

    if (!this.form.valid) {
      setFormTouched(this.form);
      this.alert.toastWarning('WARNING_FORM_INVALID');
      console.log(this.form);
      return;
    }

    // Salva i sendObjects nello storage locale prima del submit
    await this.saveSendObjectsToStorage(sendObjects);

    const params = this.form.value;
    params.date = moment(params.date, INPUT_FORMAT);

    if (params.arrivalDate) {
      params.arrivalDate = moment(params.arrivalDate, INPUT_FORMAT);
    }

    if (this.verboseFromProvince) {
      params.verboseFromProvince = this.verboseFromProvince;
    }
    if (this.verboseFromRegion) {
      params.verboseFromRegion = this.verboseFromRegion;
    }
    if (this.verboseToProvince) {
      params.verboseToProvince = this.verboseToProvince;
    }
    if (this.verboseToRegion) {
      params.verboseToRegion = this.verboseToRegion;
    }

    delete params.shippingTermsAndConditions; // TIP not necessary to send to backend

    // Random string con data e alfanumerico di 8 caratteri
    params.payNowSession = `send_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;

    this.router.navigate(['/find-carry'], { queryParams: params });
  }

  lookForCarryHitToGoogleAnalytics() {
    this.googleAnalyticsService.trackClickEvent('event', 'look for carry from web', 'look for a carry', '', '', 0, false);
    this.firebaseService.firebaseLogEvent('click', { data: 'look for a carry from native' });
  }

  async jumpToChatAI() {
    await this.router.navigate(['/chat-ai']);
  }

  initRouteSteps(stepsFrom: any, stepsTo: any) {
    const { province: pfrom = '', region: rFrom = '' } = stepsFrom;
    const { province: pTo = '', region: rTo = '' } = stepsTo;

    this.verboseFromProvince = pfrom;
    this.verboseFromRegion = rFrom;
    this.verboseToProvince = pTo;
    this.verboseToRegion = rTo;
  }

  // Per aggiungere oggetti (comportamento attuale)
  async openSendObjects() {
    // this.alert.toastWarning('Apertura modal oggetti in corso...');
    try {
      const currentObjects = this.form.get('sendObjects') ? this.form.get('sendObjects').value : [];
    
    const modal = await this.modalController.create({
      component: SendObjectsPage,
      componentProps: {
        index: currentObjects.length,
        objects: currentObjects
      }
    });

    // Registra l'handler PRIMA di presentare il modal
    modal.onDidDismiss().then((result) => {
      if (result.data && result.data.objects) {
        this.form.patchValue({
          sendObjects: result.data.objects
        });
        
        const totalWeight = result.data.objects.reduce((sum, obj) => sum + (obj.weight || 0), 0);
        this.form.patchValue({
          packageWeight: totalWeight
        });
      }
    });

    console.log('Modal SendObjects creato');

    // Presenta il modal DOPO aver registrato l'handler
    await modal.present();
    } catch (error) {
      // this.alert.toastWarning('Errore nell\'apertura del form');
      this.alert.toastWarning(`Errore nell'apertura del form: ${error}`);
    }
  }

  // Per modificare un oggetto specifico
  async editSendObject(index: number) {
    const currentObjects = this.form.get('sendObjects') ? this.form.get('sendObjects').value : [];
    const objectToEdit = currentObjects[index];
    
    const modal = await this.modalController.create({
      component: SendObjectsPage,
      componentProps: {
        objects: currentObjects,
        objectToEdit: objectToEdit,
        editIndex: index
      }
    });

    modal.onDidDismiss().then((result) => {
      if (result.data && result.data.objects) {
        this.form.patchValue({
          sendObjects: result.data.objects
        });
        
        const totalWeight = result.data.objects.reduce((sum, obj) => sum + (obj.weight || 0), 0);
        this.form.patchValue({
          packageWeight: totalWeight
        });

        const packageType = result.data.objects.length > 0 ? result.data.objects[0].packageType : null;
        this.form.patchValue({
          packageType: packageType
        });
      }
    });

    return await modal.present();
  }

  getSendObjectsCount(): number {
    const sendObjectControl = this.form.get('sendObjects');
    const objects = sendObjectControl ? sendObjectControl.value : [];
    return objects ? objects.length : 0;
  }

  async confirmRemoveObject(index: number) {
    const objectName = this.form.get('sendObjects').value[index].name || 'questo oggetto';
    
    const alert = await this.alertController.create({
      header: 'Conferma rimozione',
      message: `Sei sicuro di voler rimuovere "${objectName}"?`,
      buttons: [
        {
          text: 'Annulla',
          role: 'cancel',
          cssClass: 'secondary'
        },
        {
          text: 'Rimuovi',
          cssClass: 'danger',
          handler: () => {
            this.removeObject(index);
          }
        }
      ]
    });

    await alert.present();
  }

  async removeObject(index: number) {
    const sendObjectControl = this.form.get('sendObjects');
    const objects = sendObjectControl ? sendObjectControl.value : [];
    if (objects && objects.length > index) {
      objects.splice(index, 1);
      this.form.patchValue({ sendObjects: objects });

      const totalWeight = objects.reduce((sum, obj) => sum + (obj.weight || 0), 0);
      this.form.patchValue({
        packageWeight: totalWeight
      });

      await this.saveSendObjectsToStorage(objects);
    }
  }

  private async saveSendObjectsToStorage(sendObjects: IRouteObjectDTO[]): Promise<void> {
    try {
      await this.storage.set('send_objects', sendObjects);
      console.log('SendObjects salvati nello storage:', sendObjects);
    } catch (error) {
      console.error('Errore nel salvare sendObjects nello storage:', error);
    }
  }

  private async loadSendObjectsFromStorage(): Promise<void> {
    try {
      const savedObjects = await this.storage.get('send_objects');
      if (savedObjects && Array.isArray(savedObjects) && savedObjects.length > 0) {
        this.form.patchValue({
          sendObjects: savedObjects
        });
        // Calcola il peso totale
        const totalWeight = savedObjects.reduce((sum, obj) => sum + (obj.weight || 0), 0);
        this.form.patchValue({
          packageWeight: totalWeight
        });
        console.log('SendObjects caricati dallo storage:', savedObjects);
      }
    } catch (error) {
      console.error('Errore nel caricare sendObjects dallo storage:', error);
    }
  }

  private async clearSendObjectsFromStorage(): Promise<void> {
    try {
      await this.storage.remove('send_objects');
      console.log('SendObjects rimossi dallo storage');
    } catch (error) {
      console.error('Errore nel rimuovere sendObjects dallo storage:', error);
    }
  }
}
